import {
  InteractionRequiredAuthError,
  SilentRequest,
} from '@azure/msal-browser';

import { loginRequest } from '@/authConfig';
import msalInstance from '@/hooks/useMsal';

const AUTH_INTERACTIONS_KEY = 'auth_interactions';
const MAX_AUTH_INTERACTIONS = 3;

let inflight: Promise<string> | null = null;

export class AuthFatalError extends Error {
  constructor(message = 'Authentication failed. Please sign in again.') {
    super(message);
    this.name = 'AuthFatalError';
  }
}

function getInteractionCount(): number {
  if (typeof window === 'undefined') return 0;
  const rawValue = window.sessionStorage.getItem(AUTH_INTERACTIONS_KEY);
  const parsed = Number(rawValue ?? '0');
  return Number.isFinite(parsed) ? parsed : 0;
}

function incrementInteractionCount(): number {
  if (typeof window === 'undefined') return 0;
  const nextCount = getInteractionCount() + 1;
  window.sessionStorage.setItem(AUTH_INTERACTIONS_KEY, String(nextCount));
  return nextCount;
}

export function resetAuthInteractionCount(): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(AUTH_INTERACTIONS_KEY);
}

async function getAccessTokenInternal(): Promise<string> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    throw new AuthFatalError('No active account found. Please sign in again.');
  }

  const silentRequest: SilentRequest = {
    scopes: ['api://3ac46749-417f-4cdb-a79b-a30f5fc1e63b/access_as_user'],
    account,
  };

  try {
    const response = await msalInstance.acquireTokenSilent(silentRequest);
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      const currentCount = getInteractionCount();
      if (currentCount >= MAX_AUTH_INTERACTIONS) {
        throw new AuthFatalError('Too many authentication attempts. Please sign in again.');
      }

      incrementInteractionCount();
      await msalInstance.loginRedirect(loginRequest(account.username));
      throw new AuthFatalError('Authentication redirect started.');
    }

    throw error;
  }
}

export async function getAccessToken(): Promise<string> {
  if (inflight) {
    return inflight;
  }

  inflight = getAccessTokenInternal().finally(() => {
    inflight = null;
  });

  return inflight;
}
